import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { api } from '../services/api';

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  setupRequired: boolean;
  login: (password: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => Promise<void>;
  setup: (password: string) => Promise<{ success: boolean; error?: string }>;
  checkAuth: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [setupRequired, setSetupRequired] = useState(false);

  const checkAuth = async () => {
    try {
      // Zuerst Status prüfen
      const status = await api.auth.status();
      setSetupRequired(status.setupRequired);

      if (status.setupRequired) {
        setIsAuthenticated(false);
        setIsLoading(false);
        return;
      }

      // Dann Auth prüfen
      const authCheck = await api.auth.check();
      setIsAuthenticated(authCheck.authenticated);
    } catch {
      setIsAuthenticated(false);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    checkAuth();
  }, []);

  const login = async (password: string) => {
    try {
      await api.auth.login(password);
      setIsAuthenticated(true);
      return { success: true };
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Login fehlgeschlagen';
      return { success: false, error: message };
    }
  };

  const logout = async () => {
    try {
      await api.auth.logout();
    } finally {
      setIsAuthenticated(false);
    }
  };

  const setup = async (password: string) => {
    try {
      await api.auth.setup(password);
      setSetupRequired(false);
      setIsAuthenticated(true);
      return { success: true };
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Setup fehlgeschlagen';
      return { success: false, error: message };
    }
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        isLoading,
        setupRequired,
        login,
        logout,
        setup,
        checkAuth,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
